import { useState, useEffect, useMemo } from "react";
import { Deal } from "../types";
import Forecasting from "./Forecasting";
import { currency, StatusPill, StatCard, Card, Button } from "@repo/o37-ui";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

// Helper function to calculate annual value based on billing cycle
const getAnnualValue = (dealValue: number, billingCycle?: string) => {
  if (!billingCycle) return dealValue;
  switch (billingCycle.toLowerCase()) {
    case "monthly": return dealValue * 12;
    case "quarterly": return dealValue * 4;
    default: return dealValue;
  }
};

export default function PartnerForecastingView({ partnerId }: { partnerId: string }) {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [salesGoal, setSalesGoal] = useState(0);
  const [isAnnualized, setIsAnnualized] = useState(true);

  useEffect(() => {
    if (partnerId) {
      loadData();
    }
  }, [partnerId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dealsRes, partnerRes] = await Promise.all([
        fetch(`${API_URL}/deals/partner/${partnerId}`),
        fetch(`${API_URL}/partners/${partnerId}`)
      ]);

      if (dealsRes.ok) {
        const data = await dealsRes.json();
        setDeals(data);
      }
      
      if (partnerRes.ok) {
        const data = await partnerRes.json();
        setSalesGoal(data.salesGoal || 0);
      }
    } catch (error) {
      console.error("Failed to load forecast data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate Forecast Metrics
  const metrics = useMemo(() => {
    const activeDeals = deals.filter(d => 
      ['qualified', 'pre-negotiation', 'negotiation'].includes(d.status)
    );

    const totalPipeline = activeDeals.reduce((sum, d) => {
      const value = isAnnualized ? getAnnualValue(d.dealValue, d.billingCycle) : d.dealValue;
      return sum + value;
    }, 0);
    
    const weightedPipeline = activeDeals.reduce((sum, d) => {
      const prob = d.closeProbability || 0;
      const value = isAnnualized ? getAnnualValue(d.dealValue, d.billingCycle) : d.dealValue;
      return sum + (value * (prob / 100));
    }, 0);

    // Group by Month for the Detailed Table
    const dealsByMonth: Record<string, Deal[]> = {};
    activeDeals.forEach(deal => {
      const dateStr = deal.expectedCloseDate || deal.createdAt;
      const date = new Date(dateStr);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!dealsByMonth[key]) dealsByMonth[key] = [];
      dealsByMonth[key].push(deal);
    });

    const sortedMonths = Object.keys(dealsByMonth).sort();

    // Adjust sales goal for instant view (assuming goal is annual)
    // If viewing instant revenue (e.g. monthly), we divide annual goal by 12 for approximation
    // But since "instant" just means contract value regardless of term, comparing to annual goal is tricky.
    // The prompt says "break that down on the instant view".
    // A simple approach is: if instant view, goal is displayed as-is (annual) but progress is naturally lower? 
    // No, better to show goal adjusted. 
    // Let's assume "Instant" means "Contract Value" (TCV or first invoice).
    // If annualized is false, we are looking at raw deal values.
    // The goal is an ANNUAL goal. So showing raw deal values vs annual goal is misleading.
    // We should probably just display the annual goal always, but the progress bar calculation needs to be aware.
    // Or, we display the "Annual Goal" label explicitly.
    // Actually, the prompt says "calculate that down". 
    // Let's approximate: Instant Goal = Annual Goal / 12 (Monthly approximation) 
    // This is imperfect but reasonable for a "monthly revenue" view if most deals are monthly.
    // If deals vary, "instant" is just "value at signing".
    
    const adjustedGoal = isAnnualized ? salesGoal : (salesGoal / 12); 

    return {
      totalPipeline,
      weightedPipeline,
      count: activeDeals.length,
      dealsByMonth,
      sortedMonths,
      adjustedGoal
    };
  }, [deals, isAnnualized, salesGoal]);

  if (loading) {
    return <div className="py-20 text-center text-neutral-500">Loading detailed forecast...</div>;
  }

  return (
    <div className="pb-20">
      {/* Controls */}
      <section className="px-6 sm:px-12 mt-10">
        <div className="max-w-5xl mx-auto flex items-center justify-end">
          <div className="bg-neutral-100 p-1 rounded-xl inline-flex">
            <Button
              variant={isAnnualized ? 'outline' : 'ghost'}
              size="sm"
              onClick={() => setIsAnnualized(true)}
              className={`h-auto py-1.5 ${isAnnualized ? 'bg-white text-neutral-900 shadow-sm font-medium border-transparent' : 'text-neutral-500 hover:text-neutral-900 bg-transparent border-transparent'}`}
              text="Annualized"
            />
            <Button
              variant={!isAnnualized ? 'outline' : 'ghost'}
              size="sm"
              onClick={() => setIsAnnualized(false)}
              className={`h-auto py-1.5 ${!isAnnualized ? 'bg-white text-neutral-900 shadow-sm font-medium border-transparent' : 'text-neutral-500 hover:text-neutral-900 bg-transparent border-transparent'}`}
              text="Instant (Monthly)"
            />
          </div>
        </div>
      </section>

      {/* Top Level Stats */}
      <section className="px-6 sm:px-12 mt-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard 
              label={`Weighted Forecast (${isAnnualized ? 'Annual' : 'Instant'})`} 
              value={currency(metrics.weightedPipeline)} 
              hint="Probability-adjusted value of open deals"
            />
            
            <StatCard 
              label={`Total Pipeline (${isAnnualized ? 'Annual' : 'Instant'})`} 
              value={currency(metrics.totalPipeline)} 
              hint={`Total value of ${metrics.count} open deals`}
            />

            <div className="rounded-2xl border border-neutral-200 p-4 sm:p-5">
              <div className="text-xs uppercase tracking-wider text-neutral-500">
                Goal Progress ({isAnnualized ? 'Annual Goal' : 'Monthly Target'})
              </div>
              <div className="flex items-end gap-2 mt-1">
                <div className="text-2xl font-semibold">
                  {metrics.adjustedGoal > 0 ? Math.round((metrics.weightedPipeline / metrics.adjustedGoal) * 100) : 0}%
                </div>
                <div className="text-sm text-neutral-500 mb-0.5">of {currency(metrics.adjustedGoal)}</div>
              </div>
              {/* Progress Bar */}
              <div className="w-full bg-neutral-100 h-1.5 mt-3 rounded-full overflow-hidden">
                <div 
                  className="bg-neutral-900 h-full rounded-full transition-all duration-300" 
                  style={{ width: `${Math.min((metrics.weightedPipeline / (metrics.adjustedGoal || 1)) * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Chart */}
      <section className="px-6 sm:px-12 mt-10">
        <div className="max-w-5xl mx-auto">
          <Forecasting partnerId={partnerId} allowEditing={true} />
        </div>
      </section>

      {/* Detailed Breakdown Table */}
      <section className="px-6 sm:px-12 mt-10">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-[13px] uppercase tracking-wider text-neutral-500 mb-4">Projected Closings (Detailed)</h2>
          
          <div className="space-y-8">
            {metrics.sortedMonths.map(month => {
              const monthDeals = metrics.dealsByMonth[month];
              
              const monthTotal = monthDeals.reduce((sum, d) => {
                const val = isAnnualized ? getAnnualValue(d.dealValue, d.billingCycle) : d.dealValue;
                return sum + val;
              }, 0);
              
              const monthWeighted = monthDeals.reduce((sum, d) => {
                const val = isAnnualized ? getAnnualValue(d.dealValue, d.billingCycle) : d.dealValue;
                return sum + (val * ((d.closeProbability || 0)/100));
              }, 0);

              const [y, m] = month.split('-');
              const dateObj = new Date(parseInt(y), parseInt(m)-1);

              return (
                <Card key={month} className="overflow-hidden">
                  <div className="bg-neutral-50 px-6 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <h3 className="font-semibold text-neutral-900">
                      {dateObj.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
                    </h3>
                    <div className="text-sm text-neutral-500">
                      Expected: <span className="font-medium text-neutral-900">{currency(monthWeighted)}</span>
                      <span className="mx-2 text-neutral-300">|</span>
                      Pipeline: {currency(monthTotal)}
                    </div>
                  </div>
                  
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-neutral-100">
                        <th className="text-left px-6 py-3 font-medium text-neutral-500 text-xs uppercase tracking-wider">Deal</th>
                        <th className="text-left px-6 py-3 font-medium text-neutral-500 text-xs uppercase tracking-wider">Stage</th>
                        <th className="text-right px-6 py-3 font-medium text-neutral-500 text-xs uppercase tracking-wider">Probability</th>
                        <th className="text-right px-6 py-3 font-medium text-neutral-500 text-xs uppercase tracking-wider">Value ({isAnnualized ? 'Ann.' : 'Inst.'})</th>
                        <th className="text-right px-6 py-3 font-medium text-neutral-500 text-xs uppercase tracking-wider">Weighted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthDeals.map(deal => {
                        const val = isAnnualized ? getAnnualValue(deal.dealValue, deal.billingCycle) : deal.dealValue;
                        return (
                          <tr key={deal.id} className="border-b border-neutral-50 last:border-0 hover:bg-neutral-50/50 transition-colors">
                            <td className="px-6 py-3">
                              <div className="font-medium text-neutral-900">{deal.name}</div>
                              <div className="text-xs text-neutral-500">{deal.company}</div>
                            </td>
                            <td className="px-6 py-3">
                              <StatusPill s={deal.status} />
                            </td>
                            <td className="px-6 py-3 text-right tabular-nums">
                              <div className="inline-flex items-center gap-2">
                                <div className="w-16 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full transition-all duration-300 ${
                                      (deal.closeProbability || 0) > 75 ? 'bg-emerald-500' : 
                                      (deal.closeProbability || 0) > 40 ? 'bg-amber-500' : 'bg-neutral-400'
                                    }`}
                                    style={{ width: `${deal.closeProbability || 0}%` }}
                                  />
                                </div>
                                <span>{deal.closeProbability || 0}%</span>
                              </div>
                            </td>
                            <td className="px-6 py-3 text-right tabular-nums text-neutral-600">
                              {currency(val)}
                            </td>
                            <td className="px-6 py-3 text-right tabular-nums font-medium text-neutral-900">
                              {currency(val * ((deal.closeProbability || 0) / 100))}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Card>
              );
            })}

            {metrics.sortedMonths.length === 0 && (
              <Card className="p-12">
                <div className="text-center text-neutral-500">
                  No active deals in the pipeline with expected close dates.
                </div>
              </Card>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
